import {ENV_NAME, GATSBY_EXT_CDN_URI} from 'gatsby-env-variables';

import dayjs, {Dayjs} from 'dayjs';
import UTIF from 'utif';

import {MIN_PASSWORD_LENGTH} from '../constants';
import {Policy} from '../models/asset/asset';

export function getPageHeader() {
  return ENV_NAME === 'dev' ? '💙Bilddatenbank - Test💛' : '💙Bilddatenbank💛';
}

export function parseBoolean(stringValue): boolean {
  switch (stringValue?.toLowerCase()?.trim()) {
    case 'true':
    case 'yes':
    case '1':
    case 'ja':
      return true;

    case 'false':
    case 'no':
    case '0':
    case 'nein':
    case null:
    case undefined:
    default:
      return false;
  }
}

export function getParentStructureId(id: string): string {
  const UNUSED_ID_PART = '0000';
  const idParts = id.match(/.{4}/g);
  if (!idParts) {
    throw new Error(`Fehlerhafte ID '${id}'`);
  }
  const lastPart = idParts.length - 1;
  if (!(idParts[lastPart] === UNUSED_ID_PART)) {
    idParts[lastPart] = UNUSED_ID_PART;
    return idParts.join('');
  }
  for (let i = lastPart; i >= 1; i--) {
    if (idParts[i] === UNUSED_ID_PART && !(idParts[i - 1] === UNUSED_ID_PART)) {
      idParts[i - 1] = UNUSED_ID_PART;
      return idParts.join('');
    }
  }
  throw new Error(`Keine gültige Vaterkennung für ID '${id}'`);
}

export function storeShowableTiffBlob(
  file: File,
  storeFunction: (blobFile: Blob | null) => void,
) {
  const WIDTH_AIM = 200;

  void file.arrayBuffer().then(buf => {
    try {
      const ifds = UTIF.decode(buf);
      if (!Array.isArray(ifds) || ifds.length === 0) {
        throw new Error('TIFF enthält keine Seiten');
      }
      const firstPageOfTif = ifds[0];
      UTIF.decodeImage(buf, firstPageOfTif);
      const rgba = UTIF.toRGBA8(firstPageOfTif);
      const imageWidth = firstPageOfTif.width;
      const imageHeight = firstPageOfTif.height;
      const cnv = document.createElement('canvas');
      cnv.width = imageWidth;
      cnv.height = imageHeight;
      const ctx = cnv.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D context nicht verfügbar');
      const imageData = ctx.createImageData(imageWidth, imageHeight);
      for (let i = 0; i < rgba.length; i++) {
        imageData.data[i] = rgba[i];
      }
      ctx.putImageData(imageData, 0, 0);

      const scaledCnv = document.createElement('canvas');
      const scaleFactor =
        WIDTH_AIM / (imageHeight > imageWidth ? imageHeight : imageWidth);
      scaledCnv.width = imageWidth * scaleFactor;
      scaledCnv.height = imageHeight * scaleFactor;
      const scaledCtx = scaledCnv.getContext('2d');
      if (!scaledCtx)
        throw new Error('Canvas 2D context für Skalierung nicht verfügbar');
      scaledCtx.drawImage(
        cnv,
        0,
        0,
        imageWidth * scaleFactor,
        imageHeight * scaleFactor,
      );
      scaledCnv.toBlob(b => storeFunction(b));
    } catch (e: unknown) {
      console.error(e instanceof Error ? e.message : e);
    }
  });
}

export function binaryToFile(file, name: string): File {
  const arr = file.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], name, {type: mime});
}

export const conditionsToText = {
  length: 'mind. ' + MIN_PASSWORD_LENGTH + ' Zeichen',
  lowercase: 'Kleinbuchstabe',
  uppercase: 'Großbuchstabe',
  number: 'Zahl',
  special: 'Sonderzeichen',
};

export const conditionsToRegex = {
  length: new RegExp(`.{${MIN_PASSWORD_LENGTH},}`),
  lowercase: /[a-zäöüß]/,
  uppercase: /[A-ZÄÖÜ]/,
  number: /\d/,
  special: /[~`!@#$%^&*()--+={}[\]|\\:;"'<>,.?/_₹]/,
};

export function isPassWordValid(password: string): boolean {
  for (const condition of Object.values(conditionsToRegex)) {
    if (!condition.test(password)) return false;
  }
  if (/\s/.test(password)) return false;

  return true;
}

export function isValidNumber(numStr: string): boolean {
  const regex = /^(?:\d+([.,]\d{1,2})?)$/;
  return regex.test(numStr);
}

export function isValidDate(dateStr: string): boolean {
  const regex = /^([0-2][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/;

  if (!regex.test(dateStr)) {
    return false;
  }

  const [day, month, year] = dateStr.split('.').map(num => parseInt(num, 10));

  const date = new Date(year, month - 1, day);
  return (
    date.getDate() === day &&
    date.getMonth() === month - 1 &&
    date.getFullYear() === year
  );
}
export function parseBooleanText(text: string): string {
  const parsedText = 'TRUE' === text ? 'Ja' : 'Nein';
  return parsedText;
}

export function overwriteArray<T>(
  target: (T | undefined)[],
  source: T[],
  startIndex: number,
) {
  const n = target.length;
  if (startIndex < 0 || startIndex >= n) {
    throw new RangeError(`startIndex ${startIndex} out of bounds [0, ${n})`);
  }
  const count = Math.min(source.length, n - startIndex);
  target.splice(startIndex, count, ...source.slice(0, count));
}

export function isPresent<T>(entity: T | null | undefined): entity is T {
  return entity !== null && entity !== undefined;
}

export function getCdnLink(
  downloadLink: string,
  region?: string,
  impolicy?: Policy,
): URL {
  const regionCdnUri =
    region && !impolicy
      ? `${region}.${GATSBY_EXT_CDN_URI}`
      : GATSBY_EXT_CDN_URI;

  const cleanPath = downloadLink.split('?')[0];
  const sanitizedDownload = cleanPath.replace('/images', '');

  const regionCdnUrl = new URL(
    `https://${regionCdnUri}/d110001${sanitizedDownload}`,
  );

  if (impolicy) {
    regionCdnUrl.searchParams.set('impolicy', impolicy);
  }

  return regionCdnUrl;
}

export function getDatePickerValue(value: string): Dayjs | null {
  if (!value) return null;

  const [day, month, year] = value.split('.');
  return dayjs(`${year}-${month}-${day}`);
}
